import { Injectable, inject } from '@angular/core'
import { GoogleMapsLoaderService } from '@app/services/google-maps-loader.service'
import { Observable, from } from 'rxjs'
import { switchMap, shareReplay } from 'rxjs/operators'
import { LocationResult } from '@app/v2/event/types/location-timezone.type'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { Alert } from '@app/v2/shared/enums/alert.enum'

@Injectable()
export class GooglePlacesService {
  private readonly _mapsLoader = inject(GoogleMapsLoaderService)
  private readonly _mapsReady$ = from(this._mapsLoader.load()).pipe(shareReplay(1))
  private readonly _alertService = inject(AlertService)

  /**
   * Returns place predictions as normalized LocationResult (lat/lng are 0 until place is selected and details fetched).
   */
  public getGeocodePredictions$(input: string): Observable<LocationResult[]> {
    return this.getPredictions$(input)
  }

  /**
   * Place autocomplete predictions for a search input.
   * @param {string} input - The input to fetch predictions for.
   * @returns {Observable<LocationResult[]>} An observable of LocationResult.
   */
  public getPredictions$(input: string): Observable<LocationResult[]> {
    return this._mapsReady$.pipe(switchMap(() => from(this._fetchPredictions(input))))
  }

  /**
   * Reverse geocode coordinates to a LocationResult (address, city label, optional venue name from Place Details).
   * @param {google.maps.LatLngLiteral} coords - The coordinates to reverse geocode.
   * @returns {Observable<LocationResult | null>} An observable of LocationResult or null if not found.
   */
  public reverseGeocode$(coords: google.maps.LatLngLiteral): Observable<LocationResult | null> {
    return this._mapsReady$.pipe(switchMap(() => from(this._reverseGeocodeAndBuild(coords))))
  }

  /**
   * Fetches place predictions for a given input and returns a normalized LocationResult.
   * @param {string} input - The input to fetch predictions for.
   * @returns {Observable<LocationResult[]>} An observable of LocationResult.
   */
  private async _fetchPredictions(input: string): Promise<LocationResult[]> {
    const { AutocompleteSuggestion } = (await google.maps.importLibrary('places')) as {
      AutocompleteSuggestion: typeof google.maps.places.AutocompleteSuggestion
    }
    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({ input })
    const predictions = (suggestions ?? []).map((suggestion) => suggestion.placePrediction).filter((prediction): prediction is google.maps.places.PlacePrediction => !!prediction)
    return predictions.map((prediction) => ({
      id: prediction.placeId ?? '',
      label: prediction.text?.toString() ?? '',
      lat: 0,
      lng: 0
    }))
  }

  /**
   * Fetches full place details for a given placeId and returns a normalized LocationResult.
   * @param {string} placeId - The placeId to fetch details for.
   * @returns {Observable<LocationResult>} An observable of LocationResult.
   */
  public getPlaceDetails$(placeId: string): Observable<LocationResult | null> {
    return this._mapsReady$.pipe(switchMap(() => from(this._fetchPlaceDetails(placeId))))
  }

  /**
   * Reverse geocode coords and build LocationResult (city/region label, formatted address, optional venue name from Place Details).
   * @param {google.maps.LatLngLiteral} coords - The coordinates to reverse geocode.
   * @returns {Promise<LocationResult | null>} A promise of LocationResult or null if not found.
   */
  private async _reverseGeocodeAndBuild(coords: google.maps.LatLngLiteral): Promise<LocationResult | null> {
    const geocoder = new google.maps.Geocoder()
    const response = await geocoder.geocode({ location: coords })
    const results = response.results ?? []
    const primary = this._selectHumanReadableResult(results)
    if (!primary) return null

    const cityLabel = this._extractCityFromGeocoderResult(primary)
    const venueAddress = this._extractVenueAddressFromGeocoderResult(primary)
    const venueCountry = this._extractCountryFromGeocoderResult(primary)

    let venueName = ''
    let timeZone = ''
    if (primary.place_id) {
      const details = await this._fetchPlaceDetails(primary.place_id)
      venueName = details?.venueName ?? ''
      timeZone = details?.timeZone ?? ''
    }

    return {
      id: primary.place_id ?? '',
      label: cityLabel || (primary.formatted_address ?? ''),
      lat: coords.lat,
      lng: coords.lng,
      cityLabel,
      venueName,
      venueAddress,
      venueCountry,
      timeZone
    }
  }

  /**
   * Picks the nearest human-readable geocoder result, skipping entries whose only type is `plus_code`
   * (e.g. "7GX9JCMP+2V"), which occur in remote areas and carry no usable address info.
   * Falls back to the first result when every entry is plus-code-only.
   * @param {google.maps.GeocoderResult[]} results - Geocoder results ordered from most to least specific.
   * @returns {google.maps.GeocoderResult | null} The chosen result, or null if the list is empty.
   */
  private _selectHumanReadableResult(results: google.maps.GeocoderResult[]): google.maps.GeocoderResult | null {
    if (!results.length) return null
    return results.find((result) => !this._isPlusCodeOnlyResult(result)) ?? results[0]
  }

  /**
   * Checks whether a geocoder result carries only a Plus Code and no real address info.
   * @param {google.maps.GeocoderResult} result - The geocoder result to inspect.
   * @returns {boolean} True when the result's sole type is `plus_code`.
   */
  private _isPlusCodeOnlyResult(result: google.maps.GeocoderResult): boolean {
    const types = result.types ?? []
    return types.length > 0 && types.every((type) => type === 'plus_code')
  }

  /**
   * Extracts a high-level city/region label from the geocoder result.
   * Prefer administrative_area_level_1 (e.g. governorate/emirate) to match legacy behavior.
   * @param {google.maps.GeocoderResult} result - The geocoder result.
   * @returns {string} The city/region label.
   */
  private _extractCityFromGeocoderResult(result: google.maps.GeocoderResult): string {
    const adminArea =
      result.address_components?.find((c) => c.types.includes('administrative_area_level_1')) ?? result.address_components?.find((c) => c.types.includes('locality'))
    return adminArea?.long_name ?? result.formatted_address ?? ''
  }

  /**
   * Extracts the country from the geocoder result.
   * @param {google.maps.GeocoderResult} result - The geocoder result.
   * @returns {string} The country name.
   */
  private _extractCountryFromGeocoderResult(result: google.maps.GeocoderResult): string {
    const country = result.address_components?.find((c) => c.types.includes('country'))
    return country?.long_name ?? ''
  }

  /**
   * Fetches full place details for a given placeId and returns a normalized LocationResult.
   * @param {string} placeId - The placeId to fetch details for.
   * @returns {Promise<LocationResult>} A promise of LocationResult.
   */
  private async _fetchPlaceDetails(placeId: string): Promise<LocationResult> {
    const { Place } = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary
    const place = new Place({ id: placeId })
    await place.fetchFields({ fields: ['location', 'displayName', 'formattedAddress', 'addressComponents', 'timeZone'] })
    if (!place.location) {
      this._alertService.alert(Alert.ERROR, 'Place not found')
      return null
    }
    const label = (typeof place.displayName === 'string' ? place.displayName : '') || (typeof place.formattedAddress === 'string' ? place.formattedAddress : '') || placeId
    const adminAreaComponent = place.addressComponents?.find((c) => c.types?.includes('administrative_area_level_1'))
    const cityLabel = adminAreaComponent?.longText ?? ''
    const venueAddress = typeof place.formattedAddress === 'string' ? this._extractVenueAddress(place.formattedAddress) : ''
    return {
      id: placeId,
      label,
      lat: place.location.lat(),
      lng: place.location.lng(),
      cityLabel,
      venueName: typeof place.displayName === 'string' ? place.displayName : '',
      venueAddress,
      venueCountry: place.addressComponents?.find((c) => c.types?.includes('country'))?.longText ?? '',
      timeZone: (place as any).timeZone?.id ?? ''
    }
  }

  /**
   * Extracts the venue address from the place.
   * @param {string} place - The place to extract the venue address from.
   * @returns {string} The venue address.
   */
  private _extractVenueAddress(place: string): string {
    const trimmed = place.trim()
    if (!trimmed) return ''
    return trimmed.replace(/^\s*[\w\d]+\+[\w\d]+(?:\s*,\s*|\s*,|\s+)?/i, '').trim()
  }

  /**
   * Extracts the venue address from the geocoder result.
   * @param {google.maps.GeocoderResult} result - The geocoder result.
   * @returns {string} The venue address.
   */
  private _extractVenueAddressFromGeocoderResult(result: google.maps.GeocoderResult): string {
    const fromFormatted = this._extractVenueAddress(result.formatted_address ?? '')
    if (fromFormatted) return fromFormatted

    for (let address of result.address_components ?? []) {
      const candidate = this._extractVenueAddress(address.long_name ?? '')
      if (candidate) return candidate
    }
    return ''
  }
}
